/**
 * Column IDs for the leads table. Used for drag-and-drop reordering and the
 * per-user column visibility preference (persisted server-side, see
 * useLeadsTableColumnPreferences).
 * ACTION is always first, not reorderable, and cannot be hidden. STATUS is only
 * shown when the rendering context enables it (columnProps.showStatus).
 */
export const ACTION_COLUMN_ID = 'ACTION';

export const ADDRESS_COLUMN_ID = 'ADDRESS';

export const STATUS_COLUMN_ID = 'STATUS';

/**
 * Columns the user cannot hide. ACTION is also non-reorderable; ADDRESS stays
 * reorderable but is always shown (it's the primary lead identifier).
 */
export const ALWAYS_VISIBLE_COLUMN_IDS = [ACTION_COLUMN_ID, ADDRESS_COLUMN_ID];

/** view_key used by the backend user_view_preference table for this table. */
export const LEADS_TABLE_VIEW_KEY = 'leads_table';

export const LEADS_TABLE_COLUMN_IDS = {
  ACTION: ACTION_COLUMN_ID,
  ADDRESS: 'ADDRESS',
  STATUS: STATUS_COLUMN_ID,
  LATEST_PROJECT_STAGE: 'LATEST_PROJECT_STAGE',
  DESCRIPTION: 'DESCRIPTION',
  MY_NOTES: 'MY_NOTES',
  TOTAL_VALUE: 'TOTAL_VALUE',
  WORK_TYPE: 'WORK_TYPE',
  LOT_SIZE: 'LOT_SIZE',
  SECTORS: 'SECTORS',
  COMPANY: 'COMPANY',
  LAST_UPDATED: 'LAST_UPDATED',
  DATE_CREATED: 'DATE_CREATED',
};

/** Full default column order (ACTION first). */
export const ALL_COLUMN_IDS = Object.values(LEADS_TABLE_COLUMN_IDS);

/** Column IDs that can be reordered (excludes ACTION). */
export const REORDERABLE_COLUMN_IDS = ALL_COLUMN_IDS.filter(
  (id) => id !== ACTION_COLUMN_ID,
);

/** Human-readable labels shown in the column selector. */
export const COLUMN_LABELS: Record<string, string> = {
  ADDRESS: 'Address',
  STATUS: 'Status',
  LATEST_PROJECT_STAGE: 'Latest Project Stage',
  DESCRIPTION: 'Description',
  MY_NOTES: 'My Notes',
  TOTAL_VALUE: 'Total Value',
  WORK_TYPE: 'Work Type',
  LOT_SIZE: 'Lot Size',
  SECTORS: 'Sectors',
  COMPANY: 'Company',
  LAST_UPDATED: 'Last Updated',
  DATE_CREATED: 'Date Created',
};

/**
 * The single flexible column. It uses `auto` width so it absorbs any leftover
 * horizontal space, letting the other (fixed-width) columns keep their size.
 */
export const FLEX_COLUMN_ID = LEADS_TABLE_COLUMN_IDS.DESCRIPTION;

/** Per-column header/cell widths. The flex column is `auto`. */
export const COLUMN_WIDTHS: Record<string, string> = {
  ACTION: '145px',
  ADDRESS: '160px',
  LATEST_PROJECT_STAGE: '170px',
  STATUS: '85px',
  DESCRIPTION: 'auto',
  MY_NOTES: '180px',
  TOTAL_VALUE: '120px',
  WORK_TYPE: '160px',
  LOT_SIZE: '80px',
  SECTORS: '120px',
  COMPANY: '180px',
  LAST_UPDATED: '120px',
  DATE_CREATED: '120px',
};

/**
 * Numeric widths used to size the table to its visible columns. The flex
 * column contributes a base width (it grows beyond this when space allows).
 */
const COLUMN_WIDTHS_PX: Record<string, number> = {
  ACTION: 145,
  ADDRESS: 160,
  LATEST_PROJECT_STAGE: 170,
  STATUS: 85,
  DESCRIPTION: 400,
  MY_NOTES: 180,
  TOTAL_VALUE: 120,
  WORK_TYPE: 160,
  LOT_SIZE: 80,
  SECTORS: 120,
  COMPANY: 180,
  LAST_UPDATED: 120,
  DATE_CREATED: 120,
};

/** Whether the flexible (auto-width) column is among the visible columns. */
export function hasFlexColumn(visibleColumnOrder: string[]): boolean {
  return visibleColumnOrder.includes(FLEX_COLUMN_ID);
}

/**
 * Width/minWidth for the table given the currently visible columns.
 *
 * The table always fills its container (width 100%). minWidth is the sum of the
 * visible columns' widths so the table grows past the page (and scrolls) when
 * there are many columns. When the page is wider than the columns, the leftover
 * space is absorbed by the flexible column — or, when it isn't visible, by a
 * trailing spacer cell (see Header/Body) — so the fixed columns keep their set
 * size instead of stretching.
 */
export function getTableDimensions(visibleColumnOrder: string[]): {
  width: string;
  minWidth: string;
} {
  const minWidthPx = visibleColumnOrder.reduce(
    (sum, id) => sum + (COLUMN_WIDTHS_PX[id] ?? 0),
    0,
  );

  return { width: '100%', minWidth: `${minWidthPx}px` };
}

export type LeadsTableViewPreference = {
  order: string[];
  visible: string[];
};

/** Default preference: every column shown, in the natural order. */
export const DEFAULT_LEADS_TABLE_PREFERENCE: LeadsTableViewPreference = {
  order: [...ALL_COLUMN_IDS],
  visible: [...ALL_COLUMN_IDS],
};

/**
 * Normalize a preference coming from the backend (or localStorage of old) so the
 * table is always renderable, even if the stored set drifts from the current
 * column list:
 *   - order becomes a complete permutation of ALL_COLUMN_IDS (unknown ids
 *     dropped, newly-added columns appended and shown by default), ACTION first.
 *   - visible keeps only known ids and always includes ACTION; newly-added
 *     columns are visible by default.
 */
export function sanitizePreference(
  pref?: Partial<LeadsTableViewPreference> | null,
): LeadsTableViewPreference {
  const known = new Set(ALL_COLUMN_IDS);

  const rawOrder = Array.isArray(pref?.order) ? (pref?.order as string[]) : [];
  const seen = new Set<string>();
  const cleanedOrder = rawOrder.filter((id) => {
    if (!known.has(id) || seen.has(id)) return false;
    seen.add(id);

    return true;
  });
  const missing = ALL_COLUMN_IDS.filter((id) => !seen.has(id));
  const order = [ACTION_COLUMN_ID, ...cleanedOrder, ...missing].filter(
    (id, index, arr) => arr.indexOf(id) === index,
  );

  const hasStoredVisible = Array.isArray(pref?.visible);
  const rawVisible = hasStoredVisible ? (pref?.visible as string[]) : ALL_COLUMN_IDS;
  const visibleSet = new Set(rawVisible.filter((id) => known.has(id)));
  // Columns added after the preference was saved should appear by default.
  if (hasStoredVisible) {
    missing.forEach((id) => visibleSet.add(id));
  }
  ALWAYS_VISIBLE_COLUMN_IDS.forEach((id) => visibleSet.add(id));
  const visible = order.filter((id) => visibleSet.has(id));

  return { order, visible };
}

/**
 * Columns the user can toggle in the current context: everything except the
 * always-visible columns (ACTION, ADDRESS), and excluding STATUS when the
 * context doesn't surface it.
 */
export function getManageableColumnIds(order: string[], showStatus: boolean): string[] {
  const alwaysVisible = new Set(ALWAYS_VISIBLE_COLUMN_IDS);

  return order.filter(
    (id) => !alwaysVisible.has(id) && (id !== STATUS_COLUMN_ID || showStatus),
  );
}

/** The columns actually rendered: ordered, visible, and allowed by context. */
export function getDisplayColumnOrder(
  pref: LeadsTableViewPreference,
  showStatus: boolean,
): string[] {
  const visibleSet = new Set(pref.visible);
  const alwaysVisible = new Set(ALWAYS_VISIBLE_COLUMN_IDS);

  return pref.order.filter(
    (id) =>
      (alwaysVisible.has(id) || visibleSet.has(id)) &&
      (id !== STATUS_COLUMN_ID || showStatus),
  );
}

/**
 * Apply a new selection for the manageable columns in this context, preserving
 * the always-visible columns and any column not manageable here (e.g. STATUS
 * when hidden).
 */
export function applyVisibleSelection(
  pref: LeadsTableViewPreference,
  showStatus: boolean,
  selectedManageable: string[],
): LeadsTableViewPreference {
  const alwaysVisible = new Set(ALWAYS_VISIBLE_COLUMN_IDS);
  const manageable = new Set(getManageableColumnIds(pref.order, showStatus));
  const selected = new Set(selectedManageable);
  const current = new Set(pref.visible);

  const visible = pref.order.filter((id) => {
    if (alwaysVisible.has(id)) return true;
    if (manageable.has(id)) return selected.has(id);

    return current.has(id);
  });

  return { ...pref, visible };
}

function reorderArray<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...arr];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);

  return result;
}

/**
 * Move a displayed, reorderable column from one slot to another, rebuilding the
 * full order while leaving ACTION and non-displayed columns anchored in place.
 */
export function reorderColumnPreference(
  pref: LeadsTableViewPreference,
  fromId: string,
  toId: string,
  showStatus: boolean,
): LeadsTableViewPreference {
  if (fromId === ACTION_COLUMN_ID || toId === ACTION_COLUMN_ID) return pref;

  const displayedReorderable = getDisplayColumnOrder(pref, showStatus).filter(
    (id) => id !== ACTION_COLUMN_ID,
  );
  const fromIndex = displayedReorderable.indexOf(fromId);
  const toIndex = displayedReorderable.indexOf(toId);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return pref;

  const moved = reorderArray(displayedReorderable, fromIndex, toIndex);
  const displayedSet = new Set(displayedReorderable);
  let j = 0;
  const order = pref.order.map((id) => {
    if (id !== ACTION_COLUMN_ID && displayedSet.has(id)) {
      const next = moved[j] as string;
      j += 1;

      return next;
    }

    return id;
  });

  return { ...pref, order };
}
